import axios from "axios";

const QUEUE_KEY = "car_clean_requestQueue";

/**
 * Recupera o array de requestQueue do localStorage.
 * @returns {Array}
 */
function getQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Salva o array de requestQueue no localStorage.
 * @param {Array} queue
 */
function saveQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/**
 * Gera uma chave única concatenando method + url + body.
 * @param {string} method
 * @param {string} url
 * @param {*} body
 * @returns {string}
 */
function generateKey(method, url, body) {
  const bodyStr = body ? JSON.stringify(body) : "";
  return method + url + bodyStr;
}

/**
 * Adiciona um request na fila (requestQueue).
 * Se já existir um request com a mesma key, não adiciona.
 *
 * @param {Object} params
 * @param {string} params.method - GET|POST|PUT|PATCH|DELETE|OPTIONS
 * @param {string} params.url
 * @param {*} [params.body] - corpo da requisição (opcional)
 * @param {Object} [params.headers] - headers da requisição (opcional)
 * @param {number} [params.timeout=10000] - timeout em ms
 * @param {boolean} [params.addAuth=true] - adicionar Authorization header
 */
export function addRequest({
  method,
  url,
  body = null,
  headers = null,
  timeout = 10000,
  addAuth = true,
}) {
  const key = generateKey(method, url, body);

  const requestObj = {
    method,
    url,
    body,
    headers: headers || {},
    timeout,
    key,
    count: 0,
    addAuth,
  };

  const queue = getQueue();

  // Verifica se já existe um request com a mesma key
  const exists = queue.some((item) => item.key === key);

  if (!exists) {
    queue.push(requestObj);
    saveQueue(queue);
  }
}

/**
 * Aguarda um tempo em milissegundos.
 * @param {number} ms
 * @returns {Promise}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Processa a fila de requisições (requestQueue).
 * Para cada item:
 *  - Se count >= 10, remove do array.
 *  - Caso contrário, executa o request via axios.
 *    - Sucesso (status 200-399): salva resposta no localStorage e remove da fila.
 *    - Erro (status < 200 ou > 399): incrementa count em +1.
 * Aguarda 100ms entre cada item.
 */
export async function processQueue() {
  const queue = getQueue();

  if (queue.length === 0) {
    return;
  }

  // Copia para iterar; vamos montar o novo estado conforme processamos
  const updatedQueue = [];

  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];

    // Se count >= 10, remove (não adiciona no updatedQueue)
    if (item.count >= 1000) {
      continue;
    }

    // Monta os headers da requisição
    const reqHeaders = { ...item.headers };

    if (item.addAuth) {
      const jwt = sessionStorage.getItem("jwt");
      if (jwt) {
        reqHeaders["authorization"] = "Bearer " + jwt;
      }
    }

    try {
      const response = await axios({
        method: item.method,
        url: item.url,
        data: item.body,
        headers: reqHeaders,
        timeout: item.timeout,
        responseType: "json",
        // Permite que qualquer status seja tratado sem lançar erro
        validateStatus: () => true,
      });

      if (response.status >= 200 && response.status <= 399) {
        // Sucesso ou redirecionamento: salva no localStorage e remove da fila
        const result = {
          timestamp: new Date().toISOString(),
          data: response.data,
        };
        localStorage.setItem(item.key, JSON.stringify(result));
        // Não adiciona no updatedQueue (remove da fila)
      } else {
        item.count += 1;
        updatedQueue.push(item);
      }
    } catch {
      item.count += 1;
      updatedQueue.push(item);
    }

    // Aguarda 100ms entre cada item
    if (i < queue.length - 1) {
      await sleep(100);
    }
  }

  saveQueue(updatedQueue);
}
